/**
 * Feb-2026 (User-Wunsch 27.05.2026) — Führerscheinklassen-Anzeige.
 *
 * Berechnet anhand von ``displacement`` (ccm) und ``power_kw`` (kW), mit welchen
 * deutschen Führerscheinklassen das Motorrad/der Roller gefahren werden darf,
 * und rendert die zutreffenden Klassen als kompakte Badges.
 *
 * Quellen: §6 FeV (Klassen AM/A1/A2/A, B196-Erweiterung).
 *
 * Klassen-Logik:
 *   * **AM** (≥15 J): bis 50 ccm UND bis 45 km/h UND bis 4 kW (Mofa/Roller)
 *   * **A1** (≥16 J): bis 125 ccm UND bis 11 kW UND ≤ 0.1 kW/kg
 *   * **A2** (≥18 J): bis 35 kW UND ≤ 0.2 kW/kg (nicht aus ≥70-kW-Modell gedrosselt)
 *   * **A**  (≥24 J / ≥20 J nach A2): unbeschränkt
 *   * **B196** (≥25 J + Führerschein B seit ≥5 J): wie A1 — D-only
 */
import { IdCard, Info } from "lucide-react";

function computeLicenseClasses(displacement, powerKw) {
    const cc = Number(displacement) || 0;
    const kw = Number(powerKw) || 0;
    const classes = [];

    // AM: bis 50 ccm + bis 4 kW (max 45 km/h — kein direktes Feld, daher
    // konservativ über ccm + kW gefiltert)
    if (cc > 0 && cc <= 50 && kw > 0 && kw <= 4) {
        classes.push({
            code: "AM",
            label: "AM (Mofa/Roller)",
            age: "ab 15 J.",
            tone: "emerald",
        });
    }

    // A1: bis 125 ccm + bis 11 kW
    if (cc > 0 && cc <= 125 && kw > 0 && kw <= 11) {
        classes.push({
            code: "A1",
            label: "A1 (Leichtkraftrad)",
            age: "ab 16 J.",
            tone: "emerald",
        });
        // B196 (DE-only Auto-Erweiterung) ist äquivalent zu A1
        classes.push({
            code: "B196",
            label: "B196 (Auto-Erweiterung)",
            age: "ab 25 J. · B seit ≥ 5 J.",
            tone: "blue",
        });
    }

    // A2: bis 35 kW + ≤ 0.2 kW/kg (Gewichts-Check fehlt im Datenmodell,
    // daher nur kW-basiert — ist die übliche Interpretation für Inserate)
    if (kw > 0 && kw <= 35) {
        classes.push({
            code: "A2",
            label: "A2 (mittleres Krad)",
            age: "ab 18 J.",
            tone: "amber",
        });
    }

    // A: keine Beschränkung
    classes.push({
        code: "A",
        label: "A (unbeschränkt)",
        age: "ab 24 J. (oder 20 J. nach A2)",
        tone: "red",
    });

    return classes;
}

const TONE_CLS = {
    emerald: "bg-emerald-50 border-emerald-300 text-emerald-900",
    amber: "bg-amber-50 border-amber-300 text-amber-900",
    red: "bg-red-50 border-red-300 text-red-900",
    blue: "bg-blue-50 border-blue-300 text-blue-900",
};

export default function LicenseClassBadges({ displacement, powerKw, category }) {
    // Fahrräder benötigen keinen Führerschein
    if (category === "bicycle") {
        return (
            <div className="bg-white border border-gray-200 rounded-sm p-4" data-testid="license-classes-bicycle">
                <div className="flex items-center gap-2 mb-1">
                    <IdCard className="w-4 h-4 text-[#FFCC00]" />
                    <h3 className="font-display font-bold text-sm">Führerschein</h3>
                </div>
                <p className="text-xs text-gray-700">Fahrrad — kein Führerschein erforderlich.</p>
            </div>
        );
    }

    const cc = Number(displacement) || 0;
    const kw = Number(powerKw) || 0;
    if (cc === 0 && kw === 0) return null;  // Daten unvollständig — Anzeige unterdrücken

    const classes = computeLicenseClasses(cc, kw);
    if (classes.length === 0) return null;

    return (
        <div className="bg-white border border-gray-200 rounded-sm p-5" data-testid="license-classes-section">
            <div className="flex items-center gap-2 mb-2">
                <IdCard className="w-4 h-4 text-[#FFCC00]" />
                <h3 className="font-display font-bold text-base">Mit welchem Führerschein darf ich fahren?</h3>
            </div>
            <p className="text-xs text-gray-600 mb-3">
                Berechnet anhand{cc > 0 ? ` von ${cc} ccm` : ""}{kw > 0 ? `${cc > 0 ? " · " : " · "}${kw} kW (${Math.round(kw * 1.359619)} PS)` : ""}. Endgültige Klassen-Einstufung beim Hersteller / Zulassungsstelle prüfen.
            </p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="license-classes-list">
                {classes.map((c) => (
                    <li
                        key={c.code}
                        className={`border-2 rounded-sm px-3 py-2 flex items-center gap-3 ${TONE_CLS[c.tone] || TONE_CLS.amber}`}
                        data-testid={`license-class-${c.code}`}
                    >
                        <div className="font-display font-black text-lg w-12 shrink-0 text-center">{c.code}</div>
                        <div className="min-w-0 flex-1">
                            <div className="font-bold text-xs leading-tight">{c.label}</div>
                            <div className="text-[10px] opacity-75 font-mono-num">{c.age}</div>
                        </div>
                    </li>
                ))}
            </ul>
            <div className="text-[10px] text-gray-500 mt-2 flex items-start gap-1">
                <Info className="w-3 h-3 mt-0.5 shrink-0" />
                Ohne Gewähr — die exakte Einstufung hängt von Leistungs-/Gewichtsverhältnis und nationalem Recht ab.
            </div>
        </div>
    );
}
